import { ArrowRight, BadgeCheck, ExternalLink, Languages, MapPin, Search, Star, X } from 'lucide-react';
import { type FormEvent, lazy, type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { globeCircle } from './globe/camera';
import type { CountryFocus, ManagerFocus } from './globe/GlobeScene';
import { ManagerMap } from './ManagerMap';
import { OrbitalCarousel } from './OrbitalCarousel';
import { regionName } from './region';
import type { HeroHub, HeroManager, HeroProps, HeroSection } from './types';
import { useStageSize } from './useStageSize';

const GlobeScene = lazy(() => import('./globe/GlobeScene'));

const RING_RADIUS = 500;
const RING_FLATTEN = 0.2;
/** The poster image is a square crop of the globe with this much margin for the atmosphere glow. */
const POSTER_SCALE = 1.3;

function withCountry(base: string, country: string): string {
    const url = new URL(base, window.location.origin);
    url.searchParams.set('country', country);

    return url.toString();
}

function mapLinkUrl({ lat, lng }: HeroManager): string {
    return `https://www.openstreetmap.org/?mlat=${lat.toFixed(6)}&mlon=${lng.toFixed(6)}#map=15/${lat.toFixed(5)}/${lng.toFixed(5)}`;
}

interface DialogProps {
    labelledBy: string;
    closeLabel: string;
    wide?: boolean;
    onClose: () => void;
    children: ReactNode;
}

function Dialog({ labelledBy, closeLabel, wide = false, onClose, children }: DialogProps) {
    const closeRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        closeRef.current?.focus();
        const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="wop-hub-dialog" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <section className={`wop-hub-dialog__card${wide ? ' wop-hub-dialog__card--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
                <button ref={closeRef} className="wop-hub-dialog__close" type="button" aria-label={closeLabel} onClick={onClose}>
                    <X aria-hidden="true" />
                </button>
                {children}
            </section>
        </div>
    );
}

export function HeroStage({ locale, assetBase, sections, initialSection, hubs, managers, activity, strings, links }: HeroProps) {
    const initialIndex = Math.max(0, sections.findIndex((section) => section.id === initialSection));
    const [selected, setSelected] = useState<HeroSection | undefined>(sections[initialIndex]);
    const [countryQuery, setCountryQuery] = useState('');
    const [countryFocus, setCountryFocus] = useState<CountryFocus | null>(null);
    const [countryMissing, setCountryMissing] = useState(false);
    const [pickedHub, setPickedHub] = useState<HeroHub | null>(null);
    const [managerFocus, setManagerFocus] = useState<ManagerFocus | null>(null);
    const platformRef = useRef<HTMLDivElement | null>(null);
    const sceneRef = useRef<HTMLDivElement | null>(null);
    const size = useStageSize(sceneRef, platformRef);
    const circle = globeCircle(size, RING_RADIUS, RING_FLATTEN);
    const [globeReady, setGlobeReady] = useState(false);
    const handleReady = useCallback(() => setGlobeReady(true), []);

    const handleCountryResolved = useCallback((found: boolean) => setCountryMissing(!found), []);
    const handlePickManager = useCallback((manager: HeroManager) => setManagerFocus({ manager, requestId: Date.now() }), []);
    const closeHub = useCallback(() => setPickedHub(null), []);
    const closeManager = useCallback(() => setManagerFocus(null), []);

    const submitCountry = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const query = countryQuery.trim();
        setCountryMissing(false);
        setCountryFocus(query ? { query, requestId: Date.now() } : null);
    };

    const hubActivity = pickedHub ? activity[pickedHub.iso.toUpperCase()] ?? 0 : 0;
    const manager = managerFocus?.manager;

    return (
        <>
            <div ref={sceneRef} className="wop-hero__scene">
                {circle && (
                    // Static frame of the globe, shown instantly while three.js downloads and warms up.
                    <img
                        className={`wop-hero__poster${globeReady ? ' is-hidden' : ''}`}
                        src={`${assetBase}/globe/poster.webp`}
                        alt=""
                        aria-hidden="true"
                        decoding="async"
                        fetchPriority="high"
                        style={{
                            left: circle.cx - circle.r * POSTER_SCALE,
                            top: circle.cy - circle.r * POSTER_SCALE,
                            width: circle.r * POSTER_SCALE * 2,
                            height: circle.r * POSTER_SCALE * 2,
                        }}
                    />
                )}
                <Suspense fallback={null}>
                    <GlobeScene
                        assetBase={assetBase}
                        locale={locale}
                        hubs={hubs}
                        managers={managers}
                        activity={activity}
                        strings={strings}
                        size={size}
                        ringRadius={RING_RADIUS}
                        flatten={RING_FLATTEN}
                        countryFocus={countryFocus}
                        managerFocus={managerFocus}
                        paused={pickedHub !== null || managerFocus !== null}
                        onPickHub={setPickedHub}
                        onPickManager={handlePickManager}
                        onCountryResolved={handleCountryResolved}
                        onReady={handleReady}
                    />
                </Suspense>
            </div>

            <OrbitalCarousel
                ref={platformRef}
                items={sections}
                initialIndex={initialIndex}
                radius={RING_RADIUS}
                flatten={RING_FLATTEN}
                label={strings.carouselLabel}
                assetBase={assetBase}
                onSelect={setSelected}
            />

            <aside className="wop-hero__panel">
                <form className="wop-hero__country" role="search" onSubmit={submitCountry}>
                    <label className="wop-visually-hidden" htmlFor="wop-hero-country">
                        {strings.countrySearchLabel}
                    </label>
                    <MapPin aria-hidden="true" />
                    <input
                        id="wop-hero-country"
                        type="search"
                        value={countryQuery}
                        placeholder={strings.countrySearchPlaceholder}
                        autoComplete="off"
                        onChange={(event) => {
                            setCountryQuery(event.target.value);

                            if (!event.target.value.trim()) {
                                setCountryFocus(null);
                                setCountryMissing(false);
                            }
                        }}
                    />
                    <button type="submit" aria-label={strings.countrySearchSubmit}>
                        <Search aria-hidden="true" />
                    </button>
                </form>
                {countryMissing && (
                    <p className="wop-hero__country-error" role="status">
                        {strings.countryNotFound}
                    </p>
                )}

                {selected && (
                    <div className="wop-hero__selected" aria-live="polite">
                        <span className="wop-hero__selected-eyebrow">{strings.selected}</span>
                        <strong>{selected.title}</strong>
                        {selected.subtitle && <p>{selected.subtitle}</p>}
                        <a className="wop-button wop-button--primary wop-button--sm" href={selected.href}>
                            {strings.open}
                            <ArrowRight aria-hidden="true" />
                        </a>
                    </div>
                )}
            </aside>

            {pickedHub && (
                <Dialog labelledBy="wop-hub-dialog-title" closeLabel={strings.hubClose} onClose={closeHub}>
                    <span className="wop-hub-dialog__eyebrow">{strings.hubEyebrow}</span>
                    <h2 id="wop-hub-dialog-title">{pickedHub.city}</h2>
                    <p className="wop-hub-dialog__meta">
                        <MapPin aria-hidden="true" />
                        {regionName(locale, pickedHub.iso, pickedHub.country)}
                    </p>
                    <div className="wop-hub-dialog__status">
                        <strong>
                            <BadgeCheck aria-hidden="true" />
                            {strings.roles[pickedHub.role]}
                        </strong>
                        <span>{hubActivity > 0 ? strings.activityCount.replace(':count', hubActivity.toLocaleString(locale)) : strings.noActivity}</span>
                    </div>
                    <div className="wop-hub-dialog__actions">
                        <a className="wop-button wop-button--primary" href={withCountry(links.countryListings, pickedHub.country)}>
                            {strings.hubListings}
                            <ArrowRight aria-hidden="true" />
                        </a>
                        <a className="wop-button wop-button--ghost" href={withCountry(links.countryTenders, pickedHub.country)}>
                            {strings.hubTenders}
                        </a>
                    </div>
                </Dialog>
            )}

            {manager && (
                <Dialog labelledBy="wop-manager-dialog-title" closeLabel={strings.hubClose} wide onClose={closeManager}>
                    <div className="wop-manager-card">
                        <div className="wop-manager-card__profile">
                            <div className="wop-manager-card__head">
                                <span className="wop-manager-card__avatar">
                                    {manager.avatar ? <img src={manager.avatar} alt="" /> : <span>{manager.initials}</span>}
                                    {manager.online && <span className="wop-manager-card__online" title={strings.managerOnline} />}
                                </span>
                                <div>
                                    <span className="wop-hub-dialog__eyebrow">{strings.managerEyebrow}</span>
                                    <h2 id="wop-manager-dialog-title">{manager.name}</h2>
                                    {manager.rating !== null && (
                                        <p className="wop-manager-card__rating">
                                            <Star aria-hidden="true" />
                                            {manager.rating.toFixed(1)}
                                            <span>{strings.managerReviews.replace(':count', String(manager.reviews))}</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <dl className="wop-manager-card__facts">
                                <div>
                                    <dt>
                                        <MapPin aria-hidden="true" />
                                        {strings.managerLocation}
                                    </dt>
                                    <dd>
                                        {manager.location}
                                        <small>
                                            {manager.lat.toFixed(5)}, {manager.lng.toFixed(5)}
                                        </small>
                                    </dd>
                                </div>
                                {manager.languages.length > 0 && (
                                    <div>
                                        <dt>
                                            <Languages aria-hidden="true" />
                                            {strings.managerLanguages}
                                        </dt>
                                        <dd>{manager.languages.join(', ')}</dd>
                                    </div>
                                )}
                            </dl>

                            {manager.bio && <p className="wop-manager-card__bio">{manager.bio}</p>}

                            <div className="wop-hub-dialog__actions">
                                <a className="wop-button wop-button--primary" href={manager.profileUrl}>
                                    {strings.managerProfile}
                                    <ArrowRight aria-hidden="true" />
                                </a>
                                <a className="wop-button wop-button--ghost" href={manager.hireUrl}>
                                    {strings.managerHire}
                                </a>
                            </div>
                        </div>

                        <div className="wop-manager-card__map">
                            <ManagerMap manager={manager} title={strings.mapTitle} />
                            <a href={mapLinkUrl(manager)} target="_blank" rel="noopener noreferrer">
                                <ExternalLink aria-hidden="true" />
                                {strings.managerOpenMap}
                            </a>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    );
}
